import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapPin, Search, Star, Home, PawPrint, X } from "lucide-react";
import LazyImage from "../components/LazyImage";
import Pagination from "../components/Pagination";
import ContactBanner from "../components/ContactBanner";
import { API_BASE_URL } from "../config";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";

const Accommodation = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalListings, setTotalListings] = useState(0);

    // Separate state for immediate search input (non-debounced)
    const [searchInput, setSearchInput] = useState("");

    // Filter states - initialize from URL params
    const [filters, setFilters] = useState(() => {
        const listingCategoryId = searchParams.get('listing_category_id');
        const webCategoryId = searchParams.get('web_category_id');
        const furnished = searchParams.get('furnished');

        return {
            search: "",
            listing_category_id: listingCategoryId || "",
            web_category_id: webCategoryId || "",
            furnished: furnished === 'true' ? true : furnished === 'false' ? false : null,
            pet_friendly: null,
            min_star_grading: "",
        };
    });

    // Fetch categories for filter dropdown
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/public/listing-categories`);
                if (response.ok) {
                    const data = await response.json();
                    setCategories(data || []);
                }
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };
        fetchCategories();
    }, []);

    // Update filters when URL params change
    useEffect(() => {
        const listingCategoryId = searchParams.get('listing_category_id');
        const webCategoryId = searchParams.get('web_category_id');

        setFilters(prevFilters => ({
            ...prevFilters,
            listing_category_id: listingCategoryId || "",
            web_category_id: webCategoryId || ""
        }));
    }, [searchParams]);

    // Fetch selected category details when URL params change
    useEffect(() => {
        const listingCategoryId = searchParams.get('listing_category_id');
        const webCategoryId = searchParams.get('web_category_id');

        if (categories.length > 0) {
            if (listingCategoryId) {
                // Specific category selected
                const category = categories.find(cat => cat.id.toString() === listingCategoryId);
                setSelectedCategory(category || null);
            } else if (webCategoryId) {
                // Web category selected - find first matching category or create generic display
                const category = categories.find(cat => cat.web_category_id.toString() === webCategoryId);
                setSelectedCategory(category || null);
            } else {
                setSelectedCategory(null);
            }
        }
    }, [searchParams, categories]);

    // Debounce search input (500ms delay)
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setFilters(prevFilters => ({ ...prevFilters, search: searchInput }));
            setCurrentPage(1); // Reset to page 1 when search changes
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [searchInput]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.listing_category_id, filters.web_category_id, filters.pet_friendly, filters.min_star_grading]);

    // Fetch listings from API with filters
    useEffect(() => {
        const fetchListings = async () => {
            try {
                setLoading(true);

                // Build query params (mutually exclusive: listing_category_id OR web_category_id)
                const params = new URLSearchParams();
                if (filters.search) params.append('search', filters.search);

                // Send only one: listing_category_id takes priority (more specific)
                if (filters.listing_category_id) {
                    params.append('listing_category_id', filters.listing_category_id);
                } else if (filters.web_category_id) {
                    params.append('web_category_id', filters.web_category_id);
                }

                params.append('furnished', 'true'); // Always fetch furnished accommodations
                if (filters.pet_friendly !== null) params.append('pet_friendly', filters.pet_friendly);
                if (filters.min_star_grading) params.append('min_star_grading', filters.min_star_grading);
                params.append('per_page', '12');
                params.append('page', currentPage.toString());

                const queryString = params.toString();
                const url = `${API_BASE_URL}/api/public/listings${queryString ? '?' + queryString : ''}`;

                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error('Failed to fetch listings');
                }
                const data = await response.json();
                setListings(data.data || []);
                setTotalPages(data.last_page || 1);
                setTotalListings(data.total || 0);
            } catch (err) {
                setError(err.message);
                console.error('Error fetching listings:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchListings();
    }, [filters, currentPage]);

    // Helper function to calculate bedroom count
    const getBedroomCount = (listing) => {
        if (!listing.bedroom_beds || listing.bedroom_beds.length === 0) return 0;
        const uniqueBedrooms = new Set(
            listing.bedroom_beds.map(bed => bed.floor_plan_bedroom_id)
        );
        return uniqueBedrooms.size;
    };

    // Helper function to calculate bathroom count (estimated from bedroom count)
    const getBathroomCount = (listing) => {
        const bedrooms = getBedroomCount(listing);
        // Estimate: 1 bathroom per bedroom, minimum 1
        return Math.max(1, bedrooms);
    };

    // Helper function to check if has parking
    const hasParking = (listing) => {
        return listing.property_features?.some(feature =>
            feature.name.toLowerCase().includes('parking')
        ) || false;
    };

    // Helper function to get area in sqm
    const getAreaSqm = (listing) => {
        if (!listing.building?.area_sqm) return null;
        return Math.round(parseFloat(listing.building.area_sqm));
    };

    // Helper function to get property image
    const getPropertyImage = (listing) => {
        if (listing.images && listing.images.length > 0) {
            return listing.images[0].signed_url;
        }
        return "/images/placeholder image.png";
    };

    // Helper function to get property name
    const getPropertyName = (listing) => {
        return listing.building?.name || "Property";
    };

    // Helper function to get property address
    const getPropertyAddress = (listing) => {
        return listing.building?.street_address || "";
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchInput("");
        setFilters({
            search: "",
            listing_category_id: "",
            web_category_id: "",
            furnished: null,
            pet_friendly: null,
            min_star_grading: "",
        });
    };

    // Check if any filters are active
    const hasActiveFilters = () => {
        return filters.search || filters.listing_category_id || filters.web_category_id ||
               filters.furnished !== null || filters.pet_friendly !== null ||
               filters.min_star_grading;
    };

    return (
        <div className="bg-[#F2F1F0] text-[#144E73] font-sans min-h-screen pt-12 pb-20 px-6">
            <div className="max-w-7xl mx-auto">
                <h2 className=" md:text-5xl font-bold text-primary mb-6 text-center">
                    {selectedCategory ? selectedCategory.header_intro : 'Accommodation'}
                </h2>
                <p className="text-center text-lg mb-8">
                    {selectedCategory ? selectedCategory.description : 'Find your perfect stay from our collection of quality properties.'}
                </p>

                {/* Filters Section */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-primary">Filter Properties</h2>
                        {hasActiveFilters() && (
                            <button
                                onClick={clearFilters}
                                className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium"
                            >
                                <X className="w-4 h-4" />
                                Clear Filters
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="search"
                                placeholder="Search by name or location..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        {/* Category */}
                        <div className="relative">
                            <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
                            <Select
                                value={filters.listing_category_id || "all"}
                                onValueChange={(value) => setFilters({ ...filters, listing_category_id: value === "all" ? "" : value })}
                            >
                                <SelectTrigger className="pl-10">
                                    <SelectValue placeholder="All Categories" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Categories</SelectItem>
                                    {categories.map(cat => (
                                        <SelectItem key={cat.id} value={cat.id.toString()}>
                                            {cat.category_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Star Grading */}
                        <div className="relative">
                            <Star className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
                            <Select
                                value={filters.min_star_grading || "any"}
                                onValueChange={(value) => setFilters({ ...filters, min_star_grading: value === "any" ? "" : value })}
                            >
                                <SelectTrigger className="pl-10">
                                    <SelectValue placeholder="Any Star Rating" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="any">Any Star Rating</SelectItem>
                                    <SelectItem value="5">5 Stars</SelectItem>
                                    <SelectItem value="4">4+ Stars</SelectItem>
                                    <SelectItem value="3">3+ Stars</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Pet Friendly */}
                        <div className="flex items-center">
                            <label className="flex items-center cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={filters.pet_friendly === true}
                                    onChange={(e) => setFilters({
                                        ...filters,
                                        pet_friendly: e.target.checked ? true : null
                                    })}
                                    className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                <PawPrint className="ml-2 w-4 h-4 text-gray-600 group-hover:text-primary" />
                                <span className="ml-1 text-gray-700 group-hover:text-primary">Pet Friendly</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Results Count */}
                {!loading && !error && (
                    <div className="mb-4 text-gray-600">
                        Found <span className="font-semibold text-primary">{totalListings}</span> {totalListings === 1 ? 'property' : 'properties'}
                        {totalListings > 12 && (
                            <span className="ml-2">
                                (Showing {(currentPage - 1) * 12 + 1}-{Math.min(currentPage * 12, totalListings)})
                            </span>
                        )}
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="text-center py-12">
                        <p className="text-lg">Loading properties...</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="text-center py-12">
                        <p className="text-red-600">Error loading properties: {error}</p>
                    </div>
                )}

                {/* Property Grid */}
                {!loading && !error && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {listings.map((listing) => {
                            const bedroomCount = getBedroomCount(listing);
                            const bathroomCount = getBathroomCount(listing);
                            const areaSqm = getAreaSqm(listing);
                            const hasParking_ = hasParking(listing);

                            return (
                                <div
                                    key={listing.id}
                                    onClick={() => navigate(`/accommodation/${listing.id}`)}
                                    className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-200 cursor-pointer"
                                >
                                    {/* Image with Badges */}
                                    <div className="relative aspect-[4/3] w-full overflow-hidden">
                                        <LazyImage
                                            src={getPropertyImage(listing)}
                                            alt={getPropertyName(listing)}
                                            className="w-full h-full object-cover"
                                        />
                                        {/* Star Rating Badge */}
                                        {listing.star_grading && (
                                            <div className="absolute top-3 right-3 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                                <Star className="w-3 h-3 fill-white" />
                                                {listing.star_grading}
                                            </div>
                                        )}
                                        {/* Pet Friendly Badge */}
                                        {listing.pet_friendly && (
                                            <div className="absolute top-3 left-3 bg-green-600 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                                <PawPrint className="w-3 h-3" />
                                                Pet Friendly
                                            </div>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="p-4">
                                        {/* Price */}
                                        {/* <div className="mb-3">
                                            <span className="bg-primary text-white px-3 py-1 rounded text-sm font-semibold">
                                                Contact for Price
                                            </span>
                                        </div> */}

                                        {/* Property Stats */}
                                        <div className="flex items-center justify-between text-sm text-gray-600 mb-3 pb-3 border-b border-gray-200">
                                            {areaSqm && (
                                                <div className="flex flex-col items-center">
                                                    <span className="font-bold text-gray-800">{areaSqm}</span>
                                                    <span className="text-xs">m²</span>
                                                </div>
                                            )}
                                            <div className="flex flex-col items-center">
                                                <span className="font-bold text-gray-800">{bathroomCount}</span>
                                                <span className="text-xs">BATHS</span>
                                            </div>
                                            <div className="flex flex-col items-center">
                                                <span className="font-bold text-gray-800">{bedroomCount}</span>
                                                <span className="text-xs">BEDS</span>
                                            </div>
                                            <div className="flex flex-col items-center">
                                                <span className="font-bold text-gray-800">{hasParking_ ? '1' : '0'}</span>
                                                <span className="text-xs">PARKING</span>
                                            </div>
                                        </div>

                                        {/* Property Name and Address */}
                                        <div>
                                            <h3 className="text-base font-bold text-primary mb-1">
                                                {getPropertyName(listing)}
                                            </h3>
                                            <div className="flex items-start gap-1 text-sm text-gray-600">
                                                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                <p>{getPropertyAddress(listing)}</p>
                                            </div>
                                            {/* Tags */}
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {listing.furnished && (
                                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                                        Furnished
                                                    </span>
                                                )}
                                                {listing.internet && (
                                                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                                        WiFi
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        </div>

                        {/* Pagination */}
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </>
                )}

                {/* Empty State */}
                {!loading && !error && listings.length === 0 && (
                    <div className="text-center py-12 bg-white rounded-xl shadow-md">
                        <p className="text-sm text-gray-600">No properties found matching your criteria.</p>
                    </div>
                )}

                {/* Contact Banner */}
                <ContactBanner category={selectedCategory} />
            </div>
        </div>
    );
};

export default Accommodation;
